// Mapa permiso → ruta preferida por defecto para ese permiso.
// Se prioriza operación diaria (POS/Dashboard) primero.
const PERM_TO_ROUTE = {
  pos: "/pos",
  dashboard: "/dashboard",
  ventas: "/ventas",
  productos: "/productos",
  clientes: "/clientes",
  tienda_nube: "/tienda-nube",
  compras: "/compras",
  proveedores: "/proveedores",
  insumos: "/insumos",
  cajas: "/cajas",
  cashflow: "/cashflow-2",
  plan_cuentas: "/plan-cuentas",
  asientos: "/asientos",
  admin_users: "/admin-users",
  medios_pago: "/medios-pago",
  etiquetas: "/etiquetas",
  auditoria: "/auditoria",
  auditoria_salida: "/auditoria/salida",
  pasivos: "/pasivos/entidades",
  gastos_estructura: "/gastos-estructura",
  // Sub-módulos de Producción
  ordenes_trabajo: "/produccion/ordenes-trabajo",
  recepcion_mercaderia: "/produccion/recepcion",
  transformaciones: "/produccion/transformaciones",
  recetas: "/produccion/recetas",
  control_salida: "/produccion/control-salida",
  inventario: "/produccion/inventario",
  stock: "/stock",
};

// Sub-permisos que componen el legacy `produccion`. Un usuario con
// `produccion` en la DB se comporta como si tuviera todos éstos.
const PRODUCCION_SUBPERMS = [
  "ordenes_trabajo", "recepcion_mercaderia", "transformaciones",
  "recetas", "control_salida", "inventario", "stock",
];

// Expande el permiso legacy `produccion` a sus 7 sub-permisos.
// Idempotente: si ya trae los sub-permisos, no duplica.
export function expandPerms(perms) {
  const out = [];
  const seen = new Set();
  for (const p of (perms || [])) {
    if (p === "produccion") {
      for (const sub of PRODUCCION_SUBPERMS) {
        if (!seen.has(sub)) { out.push(sub); seen.add(sub); }
      }
    } else if (!seen.has(p)) {
      out.push(p); seen.add(p);
    }
  }
  return out;
}

// Prioridad al calcular el landing por defecto. El primero que el usuario tenga.
// `pos` va AL FINAL: si el usuario tiene otros permisos, usamos ésos como
// landing. POS sólo se elige de default cuando es el único permiso — así los
// usuarios híbridos (POS + otros módulos) pueden navegar al ERP con el botón
// "Salir" del POS sin quedar rebotando de vuelta al mostrador.
const DEFAULT_ORDER = [
  "dashboard",
  "ventas",
  "ordenes_trabajo",
  "recepcion_mercaderia",
  "transformaciones",
  "recetas",
  "control_salida",
  "inventario",
  "stock",
  "productos",
  "clientes",
  "compras",
  "proveedores",
  "cajas",
  "tienda_nube",
  "cashflow",
  "plan_cuentas",
  "asientos",
  "insumos",
  "admin_users",
  "medios_pago",
  "etiquetas",
  "auditoria",
  "auditoria_salida",
  "pasivos",
  "gastos_estructura",
  "pos",   // último — sólo landing si el user es POS-only
];

// Rutas disponibles → permiso necesario.
const ROUTE_TO_PERM = {
  "/pos": "pos",
  "/dashboard": "dashboard",
  "/ventas": "ventas",
  "/pedidos": "ventas",
  "/promociones": "ventas",
  "/rec": "ventas",
  "/logistica": "ventas",
  "/produccion/ordenes-trabajo": "ordenes_trabajo",
  "/produccion/recepcion": "recepcion_mercaderia",
  "/produccion/transformaciones": "transformaciones",
  "/produccion/recetas": "recetas",
  "/produccion/control-salida": "control_salida",
  "/produccion/inventario": "inventario",
  "/stock": "stock",
  "/productos": "productos",
  "/clientes": "clientes",
  "/tienda-nube": "tienda_nube",
  "/compras": "compras",
  "/proveedores": "proveedores",
  "/insumos": "insumos",
  "/cajas": "cajas",
  "/movimientos-cajas": "cajas",
  "/cheques": "cajas",
  "/cashflow-2": "cashflow",
  "/plan-cuentas": "plan_cuentas",
  "/sumas-saldos": "plan_cuentas",
  "/asientos": "asientos",
  "/admin-users": "admin_users",
  "/mantenimiento": "admin_users",
  "/medios-pago": "medios_pago",
  "/etiquetas": "etiquetas",
  "/auditoria": "auditoria",
  "/auditoria/salida": "auditoria_salida",
  "/pasivos": "pasivos",
  "/pasivos/entidades": "pasivos",
  "/pasivos/saldos": "pasivos",
  "/gastos-estructura": "gastos_estructura",
};

export function getDefaultRoute(user) {
  if (!user) return "/login";
  if (user.is_admin) return "/dashboard";
  const perms = expandPerms(user.permissions);
  for (const p of DEFAULT_ORDER) {
    if (perms.includes(p) && PERM_TO_ROUTE[p]) return PERM_TO_ROUTE[p];
  }
  return "/dashboard"; // fallback aunque no tenga acceso (mostrará vacío)
}

// Landing "secundario" — el próximo módulo distinto de la ruta actual.
// Se usa desde POS para que el botón "Salir" mande al ERP cuando el user
// tiene POS + otros módulos, en vez de rebotar de vuelta al mostrador.
export function getSecondaryRoute(user, excludePerm = "pos") {
  if (!user) return "/login";
  if (user.is_admin) return "/dashboard";
  const perms = expandPerms(user.permissions).filter((p) => p !== excludePerm);
  for (const p of DEFAULT_ORDER) {
    if (p === excludePerm) continue;
    if (perms.includes(p) && PERM_TO_ROUTE[p]) return PERM_TO_ROUTE[p];
  }
  return null; // no hay otro módulo → el POS quedará como único
}

export function canAccessRoute(user, pathname) {
  if (!user) return false;
  if (user.is_admin) return true;
  const perm = ROUTE_TO_PERM[pathname];
  if (!perm) return true; // ruta sin restricción explícita
  return expandPerms(user.permissions).includes(perm);
}

// Devuelve `true` si el usuario tiene el módulo (permiso) de la ruta actual
// marcado como solo-lectura. Admins nunca están en solo-lectura.
export function isRouteReadOnly(user, pathname) {
  if (!user || user.is_admin) return false;
  const perm = ROUTE_TO_PERM[pathname];
  if (!perm) return false;
  return (user.permissions_readonly || []).includes(perm);
}

// Devuelve el permiso (módulo) asociado a la ruta actual, o null.
export function getRoutePerm(pathname) {
  return ROUTE_TO_PERM[pathname] || null;
}
